import { useLayoutEffect, useRef, type RefObject } from 'react';
import { COUNT_UP_MS, easeOutCubic, prefersReducedMotion } from './motion';

export function countUpValue(target: number, elapsedMs: number): number {
  return target * easeOutCubic(Math.min(1, elapsedMs / COUNT_UP_MS));
}

function stop(node: HTMLElement): void {
  delete node.dataset.counting;
  delete node.dataset.painted;
}

export function useCountUp<T extends HTMLElement>(value: number, format: (n: number) => string): RefObject<T | null> {
  const ref = useRef<T>(null);
  const latest = useRef({ value, format });
  useLayoutEffect(() => {
    latest.current = { value, format };
  });
  // Runs once: the count-up plays on first render only. Later value changes update the real text straight away,
  // and a count still in flight reads `latest` so it lands on the new value.
  useLayoutEffect(() => {
    const node = ref.current;
    if (node === null || prefersReducedMotion()) return undefined;
    let start: number | null = null;
    let frame = 0;
    const paint = (now: number): void => {
      start ??= now;
      const elapsed = now - start;
      if (elapsed >= COUNT_UP_MS) {
        stop(node);
        return;
      }
      node.dataset.painted = latest.current.format(countUpValue(latest.current.value, elapsed));
      frame = window.requestAnimationFrame(paint);
    };
    node.dataset.counting = 'true';
    node.dataset.painted = latest.current.format(0);
    frame = window.requestAnimationFrame(paint);
    return () => {
      window.cancelAnimationFrame(frame);
      stop(node);
    };
  }, []);
  return ref;
}
